"use client";

import { useRouter } from "next/navigation";
import { useState, type FormEvent } from "react";

import type { AuthTenantCandidate } from "@/generated/api/types.gen";
import { zCreateSessionV1AuthSessionPostResponse } from "@/generated/api/zod.gen";
import { parseJsonResponse } from "@/lib/api-contract";
import type { Dictionary } from "@/lib/i18n";
import type { Locale } from "@/types/ingestion";

import { NoticeBanner } from "./notice-banner";

type LoginFormProps = {
  dictionary: Dictionary;
  locale: Locale;
  /**
   * The operator did not choose to be here: their session ended and BetQ sent
   * them back (issue #268, S6). Set from the `expired=1` flag the
   * cookie-clearing handler redirects with, never inferred from the absence of
   * a cookie — a deliberate logout also arrives at this screen with no cookie,
   * and telling that person they "have been signed out" would be noise that
   * teaches everyone to ignore the notice on the one occasion it matters.
   */
  sessionEnded?: boolean;
};

/**
 * The current host with its leading label replaced by a tenant slug --
 * `app.betq.ai` -> `auroraplay.app.betq.ai` (issue #70). The bare host is
 * always shaped `app.<zone>` by construction (`tenantSlugFromHost` on the
 * server only takes this branch when it derived no slug), so prepending
 * `<slug>.` is exactly the tenant host, no zone table needed.
 */
function tenantHostUrl(tenantSlug: string): string {
  return `${window.location.protocol}//${tenantSlug}.${window.location.host}${window.location.pathname}`;
}

export function LoginForm({
  dictionary,
  locale,
  sessionEnded = false,
}: LoginFormProps) {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSessionEnded, setShowSessionEnded] = useState(sessionEnded);
  // Issue #70, founder decision 2026-08-10: the bare host never mints a
  // session, so a submitted credential that verifies against more than one
  // operator here has to be resolved, not signed in -- reachable only after
  // the credential above already proved it, never before.
  const [candidates, setCandidates] = useState<AuthTenantCandidate[] | null>(
    null,
  );
  const [redirecting, setRedirecting] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim()) {
      return;
    }
    setPending(true);
    setError(null);
    setCandidates(null);
    // The explanation of how they got here has been read and acted on. Leaving
    // it up next to a sign-in failure would put two competing messages on a
    // 360px card, and the live one is the failure.
    setShowSessionEnded(false);
    // A local flag, not the `redirecting` state: `setRedirecting(true)` below
    // does not take effect within this same call, so reading the state back
    // in `finally` would still see the pre-call value and reset `pending`
    // right as the page is about to leave.
    let leaving = false;
    try {
      const response = await fetch("/api/auth/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!response.ok) {
        setError(dictionary.auth.error);
        return;
      }
      const body = await parseJsonResponse(
        response,
        zCreateSessionV1AuthSessionPostResponse,
      ).catch(() => null);
      if (body === null) {
        setError(dictionary.auth.error);
        return;
      }
      if ("candidates" in body) {
        const resolved = body.candidates;
        if (resolved.length === 1) {
          // Stays `pending` through the redirect -- there is no "done" state
          // to return to, the page is leaving.
          leaving = true;
          setRedirecting(true);
          window.location.assign(tenantHostUrl(resolved[0].tenant_slug));
          return;
        }
        setCandidates(resolved);
        return;
      }
      router.push(`/${locale}`);
      router.refresh();
    } catch {
      setError(dictionary.auth.error);
    } finally {
      if (!leaving) {
        setPending(false);
      }
    }
  }

  if (redirecting) {
    return <NoticeBanner message={dictionary.auth.redirecting} />;
  }

  if (candidates) {
    // Reachable only after the submitted credential already verified against
    // every one of these operators (issue #70, founder decision 2026-08-10)
    // -- nothing here is shown to anyone who has not proven a password.
    return (
      <div className="auth-form">
        <p>{dictionary.auth.chooseTenant}</p>
        <ul className="tenant-candidate-list">
          {candidates.map((candidate) => (
            <li key={candidate.tenant_slug}>
              <a href={tenantHostUrl(candidate.tenant_slug)}>
                {candidate.operator_name}
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <>
      {/* Outside the <form>, so it is a child of the auth card's grid: inside
          it, `.auth-form span` would restyle the notice text as a field label. */}
      {showSessionEnded ? (
        <NoticeBanner message={dictionary.auth.signedOut} />
      ) : null}
      <form className="auth-form" onSubmit={onSubmit}>
        {error ? <p className="inline-error">{error}</p> : null}
        <label>
          <span>{dictionary.auth.email}</span>
          <input
            autoComplete="username"
            onChange={(event) => setEmail(event.target.value)}
            type="email"
            value={email}
          />
        </label>
        <label>
          <span>{dictionary.auth.password}</span>
          <input
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
            type="password"
            value={password}
          />
        </label>
        <button className="button" disabled={pending} type="submit">
          {pending ? dictionary.auth.signingIn : dictionary.auth.signIn}
        </button>
      </form>
    </>
  );
}
